/**
 * Fixture elements for the design estimate (eval/TAKEOFF_PLAN.md): a coloured
 * container (ellipse / rounded rectangle, by the item's symbol) with a bound
 * label ("LD01", "LD01 ×4", "L01 24 ft"), tagged with customData.estimate so
 * the server can count them. Plain Excalidraw elements, so they sync, undo,
 * copy, paste and duplicate like everything else on the board.
 */
import { fixtureOf, isFixtureLabel, type FixtureData } from '../../shared/src/estimate';
import type { TakeoffSymbol } from '../../shared/src/takeoff';
import { polygonElement, shapeElement, starPoints, trianglePoints, type El } from './elements';

/** Fixture symbols are unlabelled circles / squares (labels are a zoom-dependent overlay, like zzTakeoff). */
export const FIXTURE_W = 40;
export const FIXTURE_H = 40;

export interface FixtureTarget {
  kind: 'item' | 'assembly';
  id: string;
  code: string;
  color: string;
  symbol: TakeoffSymbol;
  uom: string;
  measure: 'count' | 'linear' | 'area';
}

/** How each item symbol is drawn: native shapes where Excalidraw has one, closed polygons otherwise. */
export type SymbolKind = 'ellipse' | 'rectangle' | 'diamond' | 'polygon';
export const SYMBOL_KIND: Record<TakeoffSymbol, SymbolKind> = {
  circle: 'ellipse',
  square: 'rectangle',
  diamond: 'diamond',
  triangle: 'polygon',
  star: 'polygon',
  hexagon: 'polygon',
};

/** Outline of a polygon symbol (triangle / star / hexagon) fitting a `size` box centred at (cx, cy). */
export function symbolPoints(symbol: TakeoffSymbol, cx: number, cy: number, size: number): [number, number][] {
  if (symbol === 'triangle') return trianglePoints(cx, cy + size * 0.06, size);
  if (symbol === 'star') return starPoints(cx, cy, size / 2, size * 0.21);
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i;
    return [cx + (size / 2) * Math.cos(a), cy + (size / 2) * Math.sin(a)] as [number, number];
  });
}

/** The element kind an existing fixture is drawn with (to spot ones drawn with an out-of-date symbol). */
export function kindOfElement(e: { type: string }): SymbolKind | null {
  if (e.type === 'ellipse' || e.type === 'rectangle' || e.type === 'diamond') return e.type;
  return e.type === 'line' ? 'polygon' : null;
}

/**
 * A symbol's real box. Polygon symbols (star / triangle / hexagon) are Excalidraw lines whose x / y is the
 * FIRST POINT, not the top-left corner, so their box comes from the points.
 */
export function symbolBox(e: { x: number; y: number; width: number; height: number; points?: readonly (readonly [number, number])[] }): { cx: number; cy: number; w: number; h: number; top: number; bottom: number } {
  if (e.points?.length) {
    const xs = e.points.map((p) => e.x + p[0]);
    const ys = e.points.map((p) => e.y + p[1]);
    const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0, top: y0, bottom: y1 };
  }
  return { cx: e.x + e.width / 2, cy: e.y + e.height / 2, w: e.width, h: e.height, top: e.y, bottom: e.y + e.height };
}

/** A count fixture drawn as a polygon is a line element too; runs are lines/arrows that are *not* count symbols. */
export function isRunLike(e: { type: string; customData?: Record<string, unknown> | null }): boolean {
  if (e.type !== 'line' && e.type !== 'arrow') return false;
  const f = fixtureOf(e as never);
  // A count fixture drawn as a line is a polygon symbol — unless it counts along the run.
  return !(f?.role === 'fixture' && (f.measure ?? 'count') === 'count' && !f.along_run);
}

const nonce = () => Math.floor(Math.random() * 2 ** 31);

/** Readable text colour on the fixture's fill. */
export function inkOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#1e1e1e' : '#ffffff';
}

/** A fixture symbol's diameter is this share of the drawing's width (≈ 0.2" on a 17" sheet), like a plotted CAD symbol. */
export const FIXTURE_SHEET_SHARE = 0.012;

/**
 * Size factor for a fixture placed at scene (x, y): fixed relative to the topmost drawing image under the
 * point, so every symbol on a drawing is the same size and scales with it (not with the zoom at placement).
 * Off any drawing it falls back to `fallback` (the last on-drawing size, or 1).
 */
export function fixtureScaleAt(scene: readonly SceneEl[], x: number, y: number, fallback = 1): number {
  for (let i = scene.length - 1; i >= 0; i--) {
    const e = scene[i];
    if (e.isDeleted || e.type !== 'image' || e.width <= 0) continue;
    if (x >= e.x && x <= e.x + e.width && y >= e.y && y <= e.y + e.height) {
      return Math.max(0.02, (e.width * FIXTURE_SHEET_SHARE) / FIXTURE_W);
    }
  }
  return fallback;
}

/** Font size that keeps a fixture label on one line inside its symbol. */
export function fixtureFontSize(label: string, w: number, shape: 'ellipse' | 'rectangle', scale: number): number {
  const inner = w * (shape === 'ellipse' ? 0.62 : 0.86);
  const longest = Math.max(1, ...label.split('\n').map((l) => l.length));
  return Math.max(0.5, Math.min(13 * scale, inner / (longest * 0.62)));
}

/** Elements for one fixture centred at scene (cx, cy), `scale` × the standard symbol size. */
export function fixtureElements(
  id: string,
  t: FixtureTarget,
  cx: number,
  cy: number,
  opts: { frameId: string | null; scale: number; qty?: number | null; length?: number | null; placedAt?: number },
): El[] {
  const w = FIXTURE_W * opts.scale;
  const h = FIXTURE_H * opts.scale;
  const est: FixtureData = {
    v: 1,
    role: 'fixture',
    ...(t.kind === 'item' ? { item_id: t.id } : { assembly_id: t.id }),
    placed_at: opts.placedAt ?? Date.now(),
    measure: t.measure,
    ...(opts.qty && opts.qty !== 1 ? { qty: opts.qty } : {}),
    ...(opts.length ? { length: opts.length } : {}),
  };
  const kind = SYMBOL_KIND[t.symbol] ?? 'ellipse';
  const style = {
    strokeColor: t.color,
    backgroundColor: t.color,
    fillStyle: 'solid' as const,
    strokeWidth: 1,
    roughness: 0,
    frameId: opts.frameId,
    customData: { estimate: est },
  };
  // No bound text: at drawing scale a symbol is ~16 px and Excalidraw's fixed text padding wraps any label
  // one letter per line (and grows the shape into a tall oval). The board draws labels as an overlay instead.
  const el: El =
    kind === 'polygon'
      ? { ...polygonElement(id, { ...style, points: symbolPoints(t.symbol, cx, cy, w), roundness: null }), polygon: true }
      : shapeElement(kind, id, { ...style, x: cx - w / 2, y: cy - h / 2, width: w, height: h, roundness: kind === 'rectangle' ? { type: 3 } : null });
  return [{ ...el, boundElements: null, versionNonce: nonce(), seed: nonce() }];
}

export interface SceneEl {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isDeleted?: boolean;
  frameId?: string | null;
  containerId?: string | null;
  customData?: Record<string, unknown>;
  points?: readonly (readonly [number, number])[];
  name?: string | null;
  version?: number;
  text?: string;
  strokeColor?: string;
  backgroundColor?: string;
  boundElements?: readonly { id: string; type: string }[] | null;
}

/** Scene bits the estimate panel and the server care about: fixtures and frames (ids + versions). */
export function estimateSignature(els: readonly SceneEl[]): string {
  let s = '';
  for (const e of els) {
    if (e.type === 'frame' || e.type === 'magicframe' || (e.customData as { estimate?: unknown } | undefined)?.estimate) {
      s += `${e.id}:${e.version ?? 0}:${e.isDeleted ? 1 : 0};`;
    }
  }
  return s;
}

export function framesOf(els: readonly SceneEl[]): SceneEl[] {
  return els.filter((e) => (e.type === 'frame' || e.type === 'magicframe') && !e.isDeleted);
}

export function frameName(f: SceneEl, index: number): string {
  return (f.name ?? '').trim() || `Frame ${index + 1}`;
}

/** The smallest live frame containing the point. */
export function frameAtPoint(els: readonly SceneEl[], x: number, y: number): SceneEl | null {
  const hits = framesOf(els).filter((f) => x >= f.x && x <= f.x + f.width && y >= f.y && y <= f.y + f.height);
  return hits.length ? hits.reduce((a, b) => (a.width * a.height <= b.width * b.height ? a : b)) : null;
}

export function pxLength(e: SceneEl): number {
  const p = e.points ?? [];
  let L = 0;
  for (let i = 1; i < p.length; i++) L += Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1]);
  return L;
}

export { fixtureOf, isFixtureLabel };
